import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Maximize2, Minimize2, LogIn, Armchair, Gift, PieChart, Cake, Loader2,
} from "lucide-react";
import api from "../api";

/**
 * Robust, configurable, projectable live event dashboard.
 * The organizer chooses which panels to show and can project any one of them
 * full-screen on the venue screen. Polls the check-in stats feed (which now
 * carries occupancy + demographics) every few seconds.
 * Route: /events/:eventId/live
 */

const ALL_WIDGETS = [
  { key: "arrivals", label: "Arrivals", icon: LogIn },
  { key: "occupancy", label: "Occupancy", icon: Armchair },
  { key: "gender", label: "Gender", icon: PieChart },
  { key: "age", label: "Age", icon: Cake },
  { key: "raffle", label: "Raffle", icon: Gift },
];
const GENDER_LABELS = { male: "Male", female: "Female", other: "Other", prefer_not: "Undisclosed", unknown: "Unknown" };
const AGE_LABELS = { under_18: "<18", "18_24": "18–24", "25_34": "25–34", "35_44": "35–44", "45_54": "45–54", "55_plus": "55+", unknown: "N/A" };
const PALETTE = ["#818cf8", "#f472b6", "#34d399", "#fbbf24", "#60a5fa", "#a78bfa", "#94a3b8"];

export default function LiveDashboardPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [winner, setWinner] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [focus, setFocus] = useState(null); // widget key projected full-screen
  const rootRef = useRef(null);

  const [enabled, setEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`live-widgets-${eventId}`)) || null; } catch { return null; }
  } );
  const active = enabled || ["arrivals", "occupancy", "gender", "age"];
  const toggle = (key) => {
    const next = active.includes(key) ? active.filter((k) => k !== key) : [...active, key];
    setEnabled(next);
    localStorage.setItem(`live-widgets-${eventId}`, JSON.stringify(next));
  };

  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/checkin/events/${eventId}/stats/`);
      setStats(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    }
  }, [eventId]);

  useEffect(() => {
    api.get(`/events/${eventId}/`).then((r) => setEvent(r.data)).catch(() => {});
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [eventId, poll]);

  const project = () => {
    const el = rootRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const draw = async () => {
    setDrawing(true); setWinner(null);
    try {
      const r = await api.post(`/checkin/events/${eventId}/raffle/`);
      setWinner(r.data?.winner || r.data);
    } catch { setWinner({ error: "Couldn't draw — is gamified entry enabled?" }); }
    setDrawing(false);
  };

  if (forbidden) return <Center>You don't have permission to view this event's dashboard.</Center>;
  if (!stats) return <Center><Loader2 className="spin" size={40} /></Center>;

  const widgetsToShow = focus ? [focus] : active;

  return (
    <div ref={rootRef} style={S.root}>
      <style>{css}</style>
      <header style={S.header}>
        <div>
          <div style={S.live}><span style={S.dot} /> LIVE</div>
          <h1 style={S.title}>{event?.title || stats.event_title || "Event"}</h1>
        </div>
        <div style={S.controls}>
          {ALL_WIDGETS.map((w) => (
            <button key={w.key} onClick={() => toggle(w.key)}
              style={{ ...S.chip, ...(active.includes(w.key) ? S.chipOn : {}) }}>
              <w.icon size={15} /> {w.label}
            </button>
          ))}
          <button onClick={project} style={{ ...S.chip, ...S.project }}>
            <Maximize2 size={15} /> Project
          </button>
        </div>
      </header>

      <div style={focus ? S.focusWrap : S.grid}>
        {widgetsToShow.map((key) => (
          <Panel key={key} wkey={key} focused={!!focus}
            onFocus={() => setFocus(key)} onUnfocus={() => setFocus(null)}
            stats={stats} event={event} winner={winner} drawing={drawing} onDraw={draw} />
        ))}
      </div>
    </div>
  );
}

function Panel({ wkey, stats, event, focused, onFocus, onUnfocus, winner, drawing, onDraw }) {
  const occ = stats.occupancy;
  const body = (() => {
    switch (wkey) {
      case "arrivals":
        return <Big value={stats.checked_in} sub={`of ${stats.total_tickets} checked in`}
          pct={stats.percent_in} note={`${stats.not_yet_in} still to arrive`} color="#34d399" />;
      case "occupancy":
        if (!occ || !occ.total_seats) return <Muted>No seating layout for this event.</Muted>;
        return (
          <div>
            <Big value={occ.arrived} sub={`of ${occ.total_seats} seats filled`}
              pct={occ.total_seats ? Math.round((occ.arrived / occ.total_seats) * 100) : 0} color="#34d399" />
            <div style={S.legend}>
              <Dot c="#34d399" /> Arrived {occ.arrived}
              <Dot c="#fbbf24" /> Assigned {Math.max(0, occ.assigned - occ.arrived)}
              <Dot c="#334155" /> Empty {occ.empty}
            </div>
          </div>
        );
      case "gender":
        return <Bars data={stats.demographics?.gender} labels={GENDER_LABELS} />;
      case "age":
        return <Bars data={stats.demographics?.age_buckets} labels={AGE_LABELS} />;
      case "raffle":
        if (!event?.enable_gamified_entry) return <Muted>Enable gamified entry to run a raffle.</Muted>;
        return (
          <div style={{ textAlign: "center" }}>
            {winner?.name ? <div style={S.winner}>🎉 {winner.name}</div>
              : winner?.error ? <Muted>{winner.error}</Muted>
              : <Muted>Draw a lucky winner from everyone checked in.</Muted>}
            <button onClick={onDraw} disabled={drawing} style={S.drawBtn}>
              {drawing ? "Drawing…" : "Draw winner"}
            </button>
          </div>
        );
      default: return null;
    }
  })();

  const meta = ALL_WIDGETS.find((w) => w.key === wkey);
  return (
    <section style={{ ...S.panel, ...(focused ? S.panelFocused : {}) }}>
      <div style={S.panelHead}>
        <span style={S.panelTitle}>{meta && <meta.icon size={16} />} {meta?.label}</span>
        {focused ? <button style={S.iconBtn} onClick={onUnfocus}><Minimize2 size={16} /></button>
          : <button style={S.iconBtn} onClick={onFocus}><Maximize2 size={16} /></button>}
      </div>
      <div style={S.panelBody}>{body}</div>
    </section>
  );
}

const Big = ({ value, sub, pct, note, color }) => (
  <div>
    <div style={{ ...S.bigNum, color }}>{value ?? 0}</div>
    <div style={S.bigSub}>{sub}</div>
    {typeof pct === "number" && (
      <div style={S.track}><div style={{ ...S.fill, width: `${Math.min(100, pct)}%`, background: color }} /></div>
    )}
    {note && <div style={S.note}>{note}</div>}
  </div>
);

const Bars = ({ data, labels }) => {
  if (!data) return <Muted>No data yet.</Muted>;
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  if (!entries.length) return <Muted>No data yet — populates as guests share it.</Muted>;
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {entries.map(([k, v], i) => (
        <div key={k}>
          <div style={S.barTop}><span>{labels[k] || k}</span><b>{v}</b></div>
          <div style={S.track}><div style={{ ...S.fill, width: `${(v / max) * 100}%`, background: PALETTE[i % PALETTE.length] }} /></div>
        </div>
      ))}
    </div>
  );
};

const Dot = ({ c }) => <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: c, margin: "0 6px 0 14px" }} />;
const Muted = ({ children }) => <div style={S.note}>{children}</div>;
const Center = ({ children }) => <div style={{ ...S.root, display: "grid", placeItems: "center" }}><style>{css}</style>{children}</div>;

const css = `.spin{animation:sp 1s linear infinite}@keyframes sp{to{transform:rotate(360deg)}}`;
const S = {
  root: { minHeight: "100vh", background: "#0b1120", color: "#e5e7eb", fontFamily: "system-ui,sans-serif", padding: "clamp(16px,3vw,32px)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 24 },
  live: { display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "monospace", fontSize: 12, letterSpacing: 2, color: "#f87171", fontWeight: 700 },
  dot: { width: 9, height: 9, borderRadius: 9, background: "#f87171", boxShadow: "0 0 0 0 rgba(248,113,113,.6)", animation: "sp 2s" },
  title: { margin: "4px 0 0", fontSize: "clamp(1.4rem,3vw,2.2rem)", letterSpacing: "-.02em" },
  controls: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10, border: "1px solid #1e293b", background: "#0f172a", color: "#94a3b8", fontSize: 13, cursor: "pointer" },
  chipOn: { background: "#312e81", borderColor: "#4f46e5", color: "#e0e7ff" },
  project: { background: "#4f46e5", borderColor: "#4f46e5", color: "#fff" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 },
  focusWrap: { display: "grid", placeItems: "stretch", minHeight: "72vh" },
  panel: { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 18, overflow: "hidden", display: "flex", flexDirection: "column" },
  panelFocused: { minHeight: "72vh" },
  panelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #1e293b" },
  panelTitle: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "#cbd5e1", fontWeight: 600 },
  iconBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", padding: 4 },
  panelBody: { padding: "clamp(18px,3vw,34px)", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" },
  bigNum: { fontSize: "clamp(3rem,10vw,7rem)", fontWeight: 800, lineHeight: 1, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums" },
  bigSub: { color: "#94a3b8", marginTop: 6, fontSize: "clamp(.9rem,2vw,1.2rem)" },
  note: { color: "#64748b", marginTop: 10, fontSize: 14 },
  track: { height: 12, borderRadius: 8, background: "#1e293b", marginTop: 14, overflow: "hidden" },
  fill: { height: "100%", borderRadius: 8, transition: "width .6s ease" },
  legend: { display: "flex", alignItems: "center", flexWrap: "wrap", marginTop: 18, fontSize: 14, color: "#cbd5e1" },
  barTop: { display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 4 },
  winner: { fontSize: "clamp(1.6rem,5vw,3rem)", fontWeight: 800, color: "#fbbf24", marginBottom: 14 },
  drawBtn: { marginTop: 8, padding: "12px 24px", borderRadius: 12, border: "none", background: "#4f46e5", color: "#fff", fontSize: 16, fontWeight: 600, cursor: "pointer" },
};
